import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../../api/axiosInstance';
import AlertModal from '../../../reusable/AlertModal';
import imageCompression from 'browser-image-compression';
import { createPortal } from 'react-dom';

const KYC = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  
  const [kycStatus, setKycStatus] = useState('none');
  const [kycData, setKycData] = useState(null); 
  const [fullDetails, setFullDetails] = useState(null); 
  
  const [globalAlert, setGlobalAlert] = useState({ isOpen: false, title: "", message: "" });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isKycSuccessOpen, setIsKycSuccessOpen] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [showResubmitForm, setShowResubmitForm] = useState(false);

  // formData holds the text inputs and LOCAL PREVIEW URLS for the UI
  const [formData, setFormData] = useState({
    aadhar: "",
    pan: "",
    aadhar_front: null,
    aadhar_back: null,
    pan_front: null,
    pan_back: null
  });

  // NEW: selectedFiles holds the actual binary Blobs to send to the server
  const [selectedFiles, setSelectedFiles] = useState({
    aadhar_front: null,
    aadhar_back: null,
    pan_front: null,
    pan_back: null
  });
  
  const [errors, setErrors] = useState({});

  const refs = {
    aadhar_front: useRef(null),
    aadhar_back: useRef(null),
    pan_front: useRef(null),
    pan_back: useRef(null)
  };

  // --- 1. LOAD KYC STATUS ---
  useEffect(() => {
    fetchKycStatus();
  }, []);

  const fetchKycStatus = async () => {
    try {
      const response = await axiosInstance.get('/kyc/get');
      if (response.data && response.data.status) {
        setKycData(response.data);
        setKycStatus(String(response.data.status).toLowerCase()); 
      } else {
        setKycStatus('none');
      }
    } catch (err) {
      const statusCode = err.response?.status;
      const backendStatus = err.response?.data?.status;

      if (statusCode === 404 || (statusCode === 400 && backendStatus === 'none')) {
        setKycStatus('none'); 
      } else {
        setGlobalAlert({ 
          isOpen: true, 
          title: "API Error", 
          message: err.response?.data?.message || "Failed to connect to the server." 
        });
        setKycStatus('none'); 
      }
    } finally {
      setLoading(false);
    }
  };

  // --- 2. VALIDATION ENGINE ---
  const isAadharValid = /^\d{12}$/.test(formData.aadhar);
  const isPanValid = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan);
  
  // CHANGED: We now validate against `selectedFiles` to ensure actual files are attached
  const isFormValid = isAadharValid && isPanValid && selectedFiles.aadhar_front && selectedFiles.aadhar_back && selectedFiles.pan_front && selectedFiles.pan_back;

  const validateField = (name, value) => {
    let errorMsg = "";
    if (value.trim() === "") return ""; // Clear error while typing if empty

    if (name === "aadhar" && value.length > 0 && !/^\d+$/.test(value)) {
        errorMsg = "Aadhar can only contain numbers.";
    } else if (name === "aadhar" && value.length === 12 && !/^\d{12}$/.test(value)) {
        errorMsg = "Aadhar must be exactly 12 digits.";
    }
    
    if (name === "pan" && value.length === 10 && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
        errorMsg = "Invalid PAN format (e.g. ABCDE1234F).";
    }
    return errorMsg;
  };

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    
    // Auto-formatting
    if (name === 'aadhar') finalValue = value.replace(/\D/g, '').substring(0, 12);
    if (name === 'pan') finalValue = value.toUpperCase().substring(0, 10);
    
    setFormData(prev => ({ ...prev, [name]: finalValue }));
    
    const err = validateField(name, finalValue);
    setErrors(prev => ({ ...prev, [name]: err }));
  };

  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2097152) {
      setGlobalAlert({ isOpen: true, title: "File Too Large", message: "Document image must be smaller than 2MB." });
      return;
    }

    try {
      // CHANGED: Compression strictly capped at 0.5MB (500KB)
      const options = { maxSizeMB: 0.5, maxWidthOrHeight: 1024, useWebWorker: true, fileType: 'image/jpeg' };
      const compressedFile = await imageCompression(file, options);
      
      // Store actual blob for API
      setSelectedFiles(prev => ({ ...prev, [fieldName]: compressedFile }));
      
      // Store local object URL for instant UI preview
      setFormData(prev => ({ ...prev, [fieldName]: URL.createObjectURL(compressedFile) }));
      setErrors(prev => ({ ...prev, [fieldName]: "" }));
    } catch (error) {
      setGlobalAlert({ isOpen: true, title: "Processing Failed", message: "Failed to process document image." });
    }
  };

  const removeImage = (fieldName) => {
    setFormData(prev => ({ ...prev, [fieldName]: null }));
    setSelectedFiles(prev => ({ ...prev, [fieldName]: null })); // Remove the blob too
    if (refs[fieldName].current) refs[fieldName].current.value = "";
  };

  // --- 3. SUBMISSION FLOW ---
  const triggerConfirm = (e) => {
    e.preventDefault();
    if (isFormValid) {
      setIsConfirmOpen(true);
    }
  };

  const executeSubmit = async () => {
    setIsConfirmOpen(false);
    setSubmitting(true);
    try {
      const uploadData = new FormData();
      uploadData.append('aadhar', formData.aadhar);
      uploadData.append('pan', formData.pan);
      uploadData.append('aadhar_front', selectedFiles.aadhar_front);
      uploadData.append('aadhar_back', selectedFiles.aadhar_back);
      uploadData.append('pan_front', selectedFiles.pan_front);
      uploadData.append('pan_back', selectedFiles.pan_back);

      await axiosInstance.post('/kyc/submit', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setKycData({
        aadhar: formData.aadhar,
        pan: formData.pan,
        status: "pending",
        submitted_on: new Date().toISOString()
      });
      
      setKycStatus('pending');
      // OPEN THE NEW SUCCESS MODAL HERE
      setIsKycSuccessOpen(true); 
      
    } catch (err) {
      setGlobalAlert({ isOpen: true, title: "Submission Failed", message: err.response?.data?.message || "Failed to submit KYC details." });
    } finally {
      setSubmitting(false);
    }
  };

  // --- 4. VIEW FULL DETAILS FLOW (UPDATED FOR INDIVIDUAL IMAGE APIs) ---
  const handleViewDetails = async () => {
    setFetchingDetails(true);
    try {
      // The image endpoints we need to hit
      const imageFields = ['aadhar_front', 'aadhar_back', 'pan_front', 'pan_back'];
      
      // Fire all 4 API requests simultaneously for maximum speed
      const imagePromises = imageFields.map(async (field) => {
        try {
          // Note: We use responseType: 'blob' because the server is serving a raw file, not JSON.
          const response = await axiosInstance.get(`/kyc/serve/${field}`, {
            responseType: 'blob' 
          });
          // Convert the raw binary blob into a temporary local URL for the <img /> tag
          return { [field]: URL.createObjectURL(response.data) };
        } catch (err) {
          console.error(`Failed to fetch ${field}:`, err);
          return { [field]: null }; // Graceful fallback if one image is missing
        }
      });

      // Wait for all 4 downloads to finish
      const imageResults = await Promise.all(imagePromises);
      
      // Merge the array of results into a single object
      const imagesMap = Object.assign({}, ...imageResults);

      // Combine the text data we already have in state with the newly fetched images
      setFullDetails({
        ...kycData,
        ...imagesMap
      });
      
      setIsViewModalOpen(true);
    } catch (err) {
      setGlobalAlert({ isOpen: true, title: "Error", message: "Failed to load KYC images." });
    } finally {
      setFetchingDetails(false);
    }
  };

  // --- UI COMPONENTS ---
  const FileUploadBox = ({ title, fieldName, subtitle }) => (
    <div className="flex flex-col">
      <div className="flex justify-between items-end mb-2">
        <label className="block text-sm font-bold text-gray-800 dark:text-slate-200">{title}</label>
        {formData[fieldName] && <span className="text-xs font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded-md">Uploaded</span>}
      </div>
      
      <div 
        onClick={() => !formData[fieldName] && refs[fieldName].current.click()}
        className={`relative w-full h-48 rounded-2xl border-2 flex flex-col items-center justify-center transition-all overflow-hidden group ${
          formData[fieldName] 
            ? 'border-indigo-200 dark:border-indigo-700 bg-indigo-50/30 dark:bg-indigo-900/20' 
            : errors[fieldName] 
              ? 'border-red-400 bg-red-50 dark:bg-red-900/20 border-dashed cursor-pointer' 
              : 'border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-dashed cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500'
        }`}
      >
        {formData[fieldName] ? (
          <>
            <img src={formData[fieldName]} alt={title} className="w-full h-full object-contain p-2" />
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <button type="button" onClick={(e) => { e.stopPropagation(); removeImage(fieldName); }} className="px-4 py-2 bg-red-500 text-white font-bold text-sm rounded-xl hover:bg-red-600 transition-transform active:scale-95 shadow-lg flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                Remove Document
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 transition-colors ${
              errors[fieldName] 
                ? 'bg-red-100 dark:bg-red-900/30 text-red-500' 
                : 'bg-white dark:bg-slate-600 text-gray-400 dark:text-slate-300 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/40 shadow-sm border border-gray-100 dark:border-slate-500'
            }`}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
            </div>
            <p className="text-sm font-bold text-gray-700 dark:text-slate-300">Click to browse files</p>
            <p className="text-xs font-semibold text-gray-400 dark:text-slate-500 mt-1 max-w-[200px] text-center">{subtitle}</p>
          </>
        )}
      </div>
      {errors[fieldName] && <p className="text-red-500 text-xs mt-2 font-medium">{errors[fieldName]}</p>}
      <input type="file" ref={refs[fieldName]} onChange={(e) => handleImageUpload(e, fieldName)} accept="image/jpeg, image/png, image/webp" className="hidden" />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 pb-10 dark:bg-slate-900 min-h-screen px-4">
      
      {/* Header & Breadcrumbs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center text-sm font-bold text-gray-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
              <Link to="/dashboard" className="inline-flex items-center text-sm font-bold text-gray-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>
                Dashboard
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-gray-400 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                <span className="ml-1 text-sm font-bold text-gray-900 dark:text-slate-200 md:ml-2">KYC Verification</span>
              </div>
            </li>
          </ol>
        </nav>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100 tracking-tight">Identity Verification (KYC)</h1>
      </div>

      {/* ── PENDING CARD ──────────────────────────────────────────────────── */}
      {kycStatus === 'pending' && (
        <div className="w-full max-w-2xl mx-auto mt-4 px-2">
          <style>{`
            .card-custom-amber { box-shadow: 0 8px 32px rgba(0,0,0,0.07); animation: rise 0.48s cubic-bezier(0.16,1,0.3,1) both; }
            .spin-ring-custom { position: relative; animation: pop 0.4s 0.3s cubic-bezier(0.34,1.56,0.64,1) both; }
            .spin-ring-custom::after { content: ''; position: absolute; inset: -4px; border-radius: 50%; border: 2px solid transparent; border-top-color: #f59e0b; border-right-color: #f59e0b; animation: spin-custom 1.4s linear infinite; }
            @keyframes spin-custom { to { transform: rotate(360deg); } }
            .badge-dot-custom { animation: blink 1.4s ease-in-out infinite; }
            @keyframes blink { 0%,100%{opacity:1;} 50%{opacity:0.3;} }
            @keyframes rise { from { opacity:0; transform:translateY(14px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
            @keyframes pop { from { transform:scale(0.5); opacity:0; } to { transform:scale(1); opacity:1; } }
          `}</style>

          <div className="card-custom-amber bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl w-full overflow-hidden">
            <div style={{ height: '3px', background: 'linear-gradient(90deg,transparent,#d97706 30%,#f59e0b 70%,transparent)' }}></div>

            <div className="card-body-custom flex flex-col items-center gap-5" style={{ padding: '36px 56px' }}>

              <div className="spin-ring-custom rounded-full flex items-center justify-center flex-shrink-0"
                   style={{ width: '64px', height: '64px', background: '#fffbeb', border: '2px solid #fcd34d' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="#d97706" strokeWidth="1.75"/>
                  <path d="M12 7v5l3 2" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              <div className="text-center">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-3"
                     style={{ background: '#fffbeb', border: '1px solid #fcd34d' }}>
                  <span className="badge-dot-custom w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#d97706' }}></span>
                  <span className="font-medium tracking-widest uppercase" style={{ fontSize: '10px', color: '#b45309' }}>Under Review</span>
                </div>
                <h2 className="font-bold mb-2 text-slate-900 dark:text-slate-100" style={{ fontFamily: "'Syne', sans-serif", fontSize: '20px', lineHeight: '1.2' }}>KYC Under Verification</h2>
                <p className="text-slate-500 dark:text-slate-400 mx-auto" style={{ fontSize: '13.5px', lineHeight: '1.65', maxWidth: '380px' }}>
                  Your documents have been submitted and are currently being reviewed. We'll notify you once the process is complete.
                </p>
              </div>

              <div className="w-full h-px bg-slate-100 dark:bg-slate-700"></div>

              <div className="meta-grid grid grid-cols-3 gap-2.5 w-full">
                {[
                  { label: 'Total Documents', value: '4 Submitted', valueClass: 'text-slate-800 dark:text-slate-200' },
                  { label: 'Submitted on', value: kycData?.submitted_on ? new Date(kycData.submitted_on).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recently', valueClass: 'text-slate-800 dark:text-slate-200' },
                  { label: 'Approved on', value: '-- / -- / --', valueClass: 'text-slate-400 dark:text-slate-500' },
                ].map((card, i) => (
                  <div key={i} className="rounded-xl p-3 text-center bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                    <p className="text-slate-500 dark:text-slate-400 mb-1 font-bold" style={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '.04em' }}>{card.label}</p>
                    <p className={`font-medium ${card.valueClass}`} style={{ fontSize: '13px' }}>{card.value}</p>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ── REJECTED CARD ─────────────────────────────────────────────────── */}
      {kycStatus === 'rejected' && !showResubmitForm && (
        <div className="w-full max-w-3xl mx-auto mt-4 px-2">
          <style>{`
            .card-custom { box-shadow: 0 8px 32px rgba(0,0,0,0.07); animation: rise 0.48s cubic-bezier(0.16,1,0.3,1) both; }
            .alert-ring-custom { animation: pop 0.4s 0.3s cubic-bezier(0.34,1.56,0.64,1) both, pulse-ring 2.6s 0.8s ease-out infinite; }
            @keyframes pulse-ring { 0%{box-shadow:0 0 0 0 rgba(220,38,38,0.25);} 70%{box-shadow:0 0 0 10px rgba(220,38,38,0);} 100%{box-shadow:0 0 0 0 rgba(220,38,38,0);} }
            .btn-main-custom { background: linear-gradient(135deg,#dc2626,#b91c1c); box-shadow: 0 3px 12px rgba(220,38,38,0.25), inset 0 1px 0 rgba(255,255,255,0.12); transition: all 0.18s ease; }
            .btn-main-custom:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(220,38,38,0.35); }
            @media (max-width: 420px) { .meta-grid{grid-template-columns:1fr 1fr!important;} .meta-last{grid-column:1 / -1!important;} .card-body-custom{padding:28px 20px!important;} }
          `}</style>

          <div className="card-custom bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl w-full overflow-hidden">
            <div style={{ height: '3px', background: 'linear-gradient(90deg,transparent,#dc2626 30%,#ef4444 70%,transparent)' }}></div>

            <div className="card-body-custom flex flex-col items-center gap-5" style={{ padding: '40px 64px' }}>

              <div className="alert-ring-custom rounded-full flex items-center justify-center flex-shrink-0"
                   style={{ width: '64px', height: '64px', background: '#fff1f1', border: '2px solid #fca5a5' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#dc2626" strokeWidth="1.75"/>
                  <path d="M12 7v5" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="16.5" r="1.1" fill="#dc2626"/>
                </svg>
              </div>

              <div className="text-center">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-3"
                     style={{ background: '#fff1f1', border: '1px solid #fca5a5' }}>
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#dc2626' }}></span>
                  <span className="font-medium tracking-widest uppercase" style={{ fontSize: '10px', color: '#dc2626' }}>Rejected</span>
                </div>
                <h2 className="font-bold mb-2 text-slate-900 dark:text-slate-100" style={{ fontFamily: "'Syne', sans-serif", fontSize: '20px', lineHeight: '1.2' }}>KYC Verification Rejected</h2>
                <p className="text-slate-500 dark:text-slate-400 mx-auto" style={{ fontSize: '13.5px', lineHeight: '1.65', maxWidth: '420px' }}>
                  Your identity verification was unsuccessful. Please review the remarks below and resubmit with the corrected documents.
                </p>
              </div>

              <div className="w-full h-px bg-slate-100 dark:bg-slate-700"></div>

              <div className="meta-grid grid grid-cols-3 gap-2.5 w-full">
                <div className="rounded-xl p-3 text-center bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                  <p className="text-slate-500 dark:text-slate-400 mb-1 font-bold" style={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '.04em' }}>Total Documents</p>
                  <p className="text-slate-800 dark:text-slate-200 font-medium" style={{ fontSize: '13px' }}>4 Submitted</p>
                </div>
                <div className="rounded-xl p-3 text-center bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                  <p className="text-slate-500 dark:text-slate-400 mb-1 font-bold" style={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '.04em' }}>Submitted on</p>
                  <p className="text-slate-800 dark:text-slate-200 font-medium" style={{ fontSize: '13px' }}>
                    {kycData?.submitted_on ? new Date(kycData.submitted_on).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recently'}
                  </p>
                </div>
                <div className="meta-last rounded-xl p-3 text-center bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                  <p className="text-red-600 dark:text-red-400 mb-1 font-bold" style={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '.04em' }}>Rejected on</p>
                  <p className="text-red-600 dark:text-red-400 font-medium" style={{ fontSize: '13px' }}>
                    {kycData?.submitted_on ? new Date(kycData.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recently'}
                  </p>
                </div>
              </div>

              {/* Remarks */}
              <div className="w-full rounded-xl p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50">
                <div className="flex items-center gap-2 mb-3">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M9 12h6m-6 4h6M7 8h10M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" stroke="#f59e0b" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="text-slate-400 dark:text-slate-500 font-medium uppercase tracking-widest" style={{ fontSize: '9.5px' }}>Reviewer Remarks</span>
                </div>
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-start gap-2.5">
                    <span className="rounded-full flex-shrink-0 mt-1.5" style={{ width: '6px', height: '6px', background: '#ef4444' }}></span>
                    <p className="text-slate-600 dark:text-slate-300" style={{ fontSize: '12.5px', lineHeight: '1.6' }}>
                      {kycData?.remarks || 'The uploaded documents were blurred or details mismatched. Please upload clear scans.'}
                    </p>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => setShowResubmitForm(true)}
                className="btn-main-custom w-full flex items-center justify-center gap-2 text-white font-medium rounded-xl border-none cursor-pointer"
                style={{ fontSize: '13.5px', padding: '12px' }}
              >
                Resubmit KYC Details
              </button>

            </div>
          </div>
        </div>
      )}

      {/* ── UPLOAD FORM ───────────────────────────────────────────────────── */}
      {(kycStatus === 'none' || kycStatus === 'unsubmitted' || (kycStatus === 'rejected' && showResubmitForm)) && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden mt-4">
          
          {kycStatus === 'rejected' && (
            <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/50 p-5 flex items-start gap-4">
              <div className="w-10 h-10 bg-white dark:bg-slate-700 border border-red-100 dark:border-red-800 text-red-600 dark:text-red-400 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <h3 className="text-base font-bold text-red-800 dark:text-red-300 mb-1">Fix and Resubmit</h3>
                  <button onClick={() => setShowResubmitForm(false)} className="text-red-400 dark:text-red-500 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium">Cancel</button>
                </div>
                <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">Please ensure you address the admin remarks below before submitting again.</p>
                <div className="bg-white dark:bg-slate-700 px-3 py-2 rounded border border-red-100 dark:border-red-800 shadow-sm inline-block">
                  <span className="text-sm font-medium text-gray-800 dark:text-slate-200">{kycData?.remarks || "Documents were unclear or details mismatched."}</span>
                </div>
              </div>
            </div>
          )}

          <div className="p-6 sm:p-10">
            <form onSubmit={triggerConfirm}>
              
              {/* SECTION 1: Identity Details */}
              <div className="mb-10">
                <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 mb-6 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-400 flex items-center justify-center text-xs">1</span>
                  Identity Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50/50 dark:bg-slate-700/30 p-6 rounded-2xl border border-gray-100 dark:border-slate-700">
                  <div className="relative">
                    <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">Aadhar Number</label>
                    <div className="relative">
                      <input 
                        type="text" 
                        name="aadhar" 
                        value={formData.aadhar} 
                        onChange={handleTextChange} 
                        placeholder="0000 0000 0000" 
                        maxLength="12"
                        className={`w-full px-4 py-3.5 rounded-xl border ${
                          errors.aadhar 
                            ? 'border-red-400 bg-red-50 dark:bg-red-900/20 dark:border-red-700' 
                            : isAadharValid 
                              ? 'border-green-400 bg-green-50/30 dark:bg-green-900/20 dark:border-green-600' 
                              : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'
                        } focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 transition-colors tracking-widest`} 
                      />
                      {isAadharValid && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-green-500">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                        </div>
                      )}
                    </div>
                    {errors.aadhar && <p className="text-red-500 text-xs mt-2 font-medium">{errors.aadhar}</p>}
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">PAN Number</label>
                    <div className="relative">
                      <input 
                        type="text" 
                        name="pan" 
                        value={formData.pan} 
                        onChange={handleTextChange} 
                        placeholder="ABCDE1234F" 
                        maxLength="10"
                        className={`w-full px-4 py-3.5 rounded-xl border ${
                          errors.pan 
                            ? 'border-red-400 bg-red-50 dark:bg-red-900/20 dark:border-red-700' 
                            : isPanValid 
                              ? 'border-green-400 bg-green-50/30 dark:bg-green-900/20 dark:border-green-600' 
                              : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'
                        } focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 transition-colors uppercase tracking-widest`} 
                      />
                      {isPanValid && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-green-500">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                        </div>
                      )}
                    </div>
                    {errors.pan && <p className="text-red-500 text-xs mt-2 font-medium">{errors.pan}</p>}
                  </div>
                </div>
              </div>

              {/* SECTION 2: Document Uploads */}
              <div className="mb-10">
                <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 mb-6 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-400 flex items-center justify-center text-xs">2</span>
                  Supporting Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <FileUploadBox title="Aadhar Card (Front)" fieldName="aadhar_front" subtitle="Clear, readable photo of the front of your Aadhar." />
                  <FileUploadBox title="Aadhar Card (Back)" fieldName="aadhar_back" subtitle="Clear photo of the back showing the address." />
                  <FileUploadBox title="PAN Card (Front)" fieldName="pan_front" subtitle="Clear photo showing your signature and face." />
                  <FileUploadBox title="PAN Card (Back)" fieldName="pan_back" subtitle="Photo of the back of your PAN card." />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between border-t border-gray-100 dark:border-slate-700 pt-8 gap-6">
                <div className="flex items-start gap-3 max-w-lg">
                  <div className="mt-0.5 text-indigo-500 dark:text-indigo-400 flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                  </div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 leading-relaxed">
                    By submitting, you legally confirm that the provided information and documents are authentic, up-to-date, and belong strictly to you.
                  </p>
                </div>
                
                <button 
                  type="submit" 
                  disabled={!isFormValid || submitting}
                  className={`w-full sm:w-auto px-10 py-4 rounded-xl font-extrabold text-sm transition-all duration-300 ${
                    !isFormValid 
                      ? 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed shadow-none' 
                      : 'bg-gray-900 dark:bg-slate-100 hover:bg-gray-800 dark:hover:bg-white text-white dark:text-slate-900 shadow-xl shadow-gray-300/50 dark:shadow-slate-900/50 active:scale-[0.98]'
                  }`}
                >
                  {submitting ? "Uploading Securely..." : "Submit Verification"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── GLOBAL MODAL ANIMATIONS ───────────────────────────────────────── */}
      <style>{`
        @keyframes blowUpModal { 0% { transform: scale(0); } 100% { transform: scale(1); } }
        @keyframes fadeInBg { 0% { background-color: rgba(17,24,39,0); backdrop-filter: blur(0px); } 100% { background-color: rgba(17,24,39,0.6); backdrop-filter: blur(4px); } }
        .bg-fade-in { animation: fadeInBg 0.5s cubic-bezier(0.165,0.840,0.440,1.000) forwards; }
        .modal-blow-up { animation: blowUpModal 0.5s cubic-bezier(0.165,0.840,0.440,1.000) forwards; }
      `}</style>

      {/* ── CONFIRMATION MODAL ────────────────────────────────────────────── */}
      {isConfirmOpen && (
        <div className="fixed top-0 left-0 w-full h-full z-[100] flex items-center justify-center p-4 bg-fade-in">
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md p-8 shadow-2xl relative modal-blow-up border border-transparent dark:border-slate-700">
            <div className="w-16 h-16 bg-amber-50 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-6 mx-auto">
              <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
            </div>
            <h3 className="text-2xl font-extrabold text-gray-900 dark:text-slate-100 text-center mb-2">Final Confirmation</h3>
            <p className="text-gray-600 dark:text-slate-400 text-center mb-8 font-medium leading-relaxed">
              Please ensure all details and images are clear and correct. <span className="text-gray-900 dark:text-slate-200 font-bold">Once submitted, you cannot edit these details</span> until the admin review is complete.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => setIsConfirmOpen(false)} className="flex-1 px-4 py-3 rounded-xl font-bold text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors">
                Cancel
              </button>
              <button onClick={executeSubmit} className="flex-1 px-4 py-3 rounded-xl font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-200 dark:shadow-indigo-900/40">
                Yes, Submit Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── APPROVED CARD ─────────────────────────────────────────────────── */}
      {kycStatus === 'approved' && (
        <div className="w-full max-w-3xl mx-auto mt-4 px-2">
          <style>{`
            .card-custom-green { box-shadow: 0 8px 32px rgba(0,0,0,0.07); animation: rise 0.48s cubic-bezier(0.16,1,0.3,1) both; }
            .check-ring-custom { animation: pop 0.4s 0.3s cubic-bezier(0.34,1.56,0.64,1) both; }
            .check-ring-custom svg { stroke-dasharray: 30; stroke-dashoffset: 30; animation: draw 0.38s 0.6s ease both; }
            @keyframes draw { to { stroke-dashoffset: 0; } }
            .btn-main-green { background: linear-gradient(135deg,#16a34a,#15803d); box-shadow: 0 3px 12px rgba(22,163,74,0.25), inset 0 1px 0 rgba(255,255,255,0.15); transition: all 0.18s ease; }
            .btn-main-green:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(22,163,74,0.35); }
            .btn-main-green:disabled { opacity: 0.7; cursor: not-allowed; }
          `}</style>

          <div className="card-custom-green bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl w-full overflow-hidden">
            <div style={{ height: '3px', background: 'linear-gradient(90deg,transparent,#16a34a 30%,#22c55e 70%,transparent)' }}></div>

            <div className="card-body-custom flex flex-col items-center gap-5" style={{ padding: '40px 44px' }}>

              <div className="check-ring-custom rounded-full flex items-center justify-center flex-shrink-0"
                   style={{ width: '64px', height: '64px', background: '#f0fdf4', border: '2px solid #bbf7d0' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              <div className="text-center">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-3"
                     style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-600 inline-block"></span>
                  <span className="text-green-700 font-medium tracking-widest uppercase" style={{ fontSize: '10px' }}>Verified</span>
                </div>
                <h2 className="font-bold mb-2 text-slate-900 dark:text-slate-100" style={{ fontFamily: "'Syne', sans-serif", fontSize: '20px', lineHeight: '1.2' }}>KYC Verification Approved</h2>
                <p className="text-slate-500 dark:text-slate-400 mx-auto" style={{ fontSize: '13.5px', lineHeight: '1.65', maxWidth: '420px' }}>
                  Your identity has been successfully verified. You now have full access to all platform features and services.
                </p>
              </div>

              <div className="w-full h-px bg-slate-100 dark:bg-slate-700"></div>

              <div className="meta-grid grid grid-cols-3 gap-2.5 w-full">
                <div className="rounded-xl p-3 text-center bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                  <p className="text-slate-500 dark:text-slate-400 mb-1 font-bold" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.04em' }}>Total Documents</p>
                  <p className="text-slate-800 dark:text-slate-200 font-medium" style={{ fontSize: '13px' }}>4 Submitted</p>
                </div>
                <div className="rounded-xl p-3 text-center bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                  <p className="text-slate-500 dark:text-slate-400 mb-1 font-bold" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.04em' }}>Submitted on</p>
                  <p className="text-slate-800 dark:text-slate-200 font-medium" style={{ fontSize: '13px' }}>
                    {kycData?.submitted_on ? new Date(kycData.submitted_on).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recently'}
                  </p>
                </div>
                <div className="meta-last rounded-xl p-3 text-center bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
                  <p className="text-slate-500 dark:text-slate-400 mb-1 font-bold" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.04em' }}>Approved on</p>
                  <p className="text-green-700 dark:text-green-400 font-medium" style={{ fontSize: '13px' }}>
                    {kycData?.submitted_on ? new Date(kycData.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recently'}
                  </p>
                </div>
              </div>

              <button 
                onClick={handleViewDetails}
                disabled={fetchingDetails}
                className="btn-main-green w-full flex items-center justify-center gap-2 text-white font-medium rounded-xl border-none cursor-pointer"
                style={{ fontSize: '13.5px', padding: '12px' }}
              >
                {fetchingDetails ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M9 12h6m-6 4h6M7 8h10M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {fetchingDetails ? "Fetching Data..." : "View Submitted Details"}
              </button>

            </div>
          </div>
        </div>
      )}

      {/* ── VIEW DETAILS MODAL ────────────────────────────────────────────── */}
      {isViewModalOpen && fullDetails && createPortal(
        <div className="fixed top-0 left-0 w-full h-full z-[9999] flex items-center justify-center p-4 sm:p-6 bg-fade-in">
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-4xl shadow-2xl relative flex flex-col max-h-[55vh] modal-blow-up border border-transparent dark:border-slate-700">

            <style>{`
              @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
              @keyframes popIn { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
            `}</style>

            <button onClick={() => setIsViewModalOpen(false)} className="absolute top-5 right-5 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors bg-white dark:bg-slate-700 rounded-full p-1 z-10">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>

            <div className="pt-8 pb-5 px-8 text-center flex-shrink-0 border-b border-gray-100 dark:border-slate-700">
              <h2 className="text-2xl font-bold text-gray-700 dark:text-slate-200">Submitted KYC Details</h2>
            </div>

            <div className="flex flex-col px-8 pb-6 overflow-y-auto custom-scrollbar">

              {/* Identity Information */}
              <div className="pb-6 pt-6">
                <h3 className="text-blue-500 dark:text-blue-400 font-bold text-sm flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"></path></svg>
                  Identity Information
                </h3>
                <div className="space-y-3 text-gray-700 dark:text-slate-300 text-base">
                  <p><span className="font-semibold text-gray-600 dark:text-slate-400 inline-block w-40">Aadhaar Number:</span> {fullDetails.aadhar}</p>
                  <p><span className="font-semibold text-gray-600 dark:text-slate-400 inline-block w-40">PAN Number:</span> <span className="uppercase">{fullDetails.pan}</span></p>
                </div>
              </div>

              {/* Status Information */}
              <div className="py-6 border-t border-gray-200 dark:border-slate-700">
                <h3 className="text-blue-500 dark:text-blue-400 font-bold text-sm flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                  Status Information
                </h3>
                <div className="space-y-3 text-gray-700 dark:text-slate-300 text-base">
                  <div className="flex items-center">
                    <span className="font-semibold text-gray-600 dark:text-slate-400 inline-block w-40">Status:</span>
                    {fullDetails.status === 'approved' && <span className="text-green-600 dark:text-green-400 px-3 py-1 rounded text-[13px] font-bold uppercase tracking-wider bg-green-50 dark:bg-green-900/30">Approved</span>}
                    {fullDetails.status === 'pending' && <span className="text-amber-600 dark:text-amber-400 px-3 py-1 rounded text-[13px] font-bold uppercase tracking-wider bg-amber-50 dark:bg-amber-900/30">Pending</span>}
                    {fullDetails.status === 'rejected' && <span className="text-red-600 dark:text-red-400 px-3 py-1 rounded text-[13px] font-bold uppercase tracking-wider bg-red-50 dark:bg-red-900/30">Rejected</span>}
                  </div>
                  <p><span className="font-semibold text-gray-600 dark:text-slate-400 inline-block w-40">Submitted On:</span> {fullDetails.submitted_on ? new Date(fullDetails.submitted_on).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A'}</p>
                  <p><span className="font-semibold text-gray-600 dark:text-slate-400 inline-block w-40">Admin Remarks:</span> {fullDetails.remarks || (fullDetails.status === 'approved' ? 'VERIFIED' : 'None')}</p>
                </div>
              </div>

              {/* Uploaded Documents */}
              <div className="pt-6 border-t border-gray-200 dark:border-slate-700">
                <h3 className="text-blue-500 dark:text-blue-400 font-bold text-sm flex items-center gap-2 mb-6">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                  Uploaded Documents
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8">
                  {[
                    { title: "Aadhaar Front:", src: fullDetails.aadhar_front },
                    { title: "Aadhaar Back:", src: fullDetails.aadhar_back },
                    { title: "PAN Front:", src: fullDetails.pan_front },
                    { title: "PAN Back:", src: fullDetails.pan_back }
                  ].map((doc, idx) => (
                    <div key={idx} className="flex flex-col">
                      <span className="font-bold text-gray-600 dark:text-slate-400 text-sm mb-2">{doc.title}</span>
                      <div className="border border-gray-200 dark:border-slate-600 rounded-lg p-1.5 bg-gray-50 dark:bg-slate-700/50 h-40 flex items-center justify-center relative overflow-hidden group">
                        {doc.src ? (
                          <>
                            <img src={doc.src} alt={doc.title} className="w-full h-full object-contain rounded" />
                            <div 
                              onClick={() => setEnlargedImage(doc.src)}
                              className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer rounded backdrop-blur-[1px]"
                            >
                              <span className="text-white text-xs font-bold px-4 py-2 bg-black/60 rounded border border-white/20 shadow-lg flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path></svg>
                                View Image
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="flex flex-col items-center opacity-50">
                            <div className="w-5 h-5 border-2 border-gray-300 dark:border-slate-500 border-t-gray-500 dark:border-t-slate-300 rounded-full animate-spin mb-2"></div>
                            <span className="text-[10px] text-gray-500 dark:text-slate-400 font-semibold uppercase">Loading</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="py-5 px-8 flex justify-center flex-shrink-0 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-slate-700 rounded-b-xl">
              <button onClick={() => setIsViewModalOpen(false)} className="bg-gray-500 dark:bg-slate-600 hover:bg-gray-600 dark:hover:bg-slate-500 text-white px-8 py-2.5 rounded text-sm font-semibold transition-colors active:scale-95 shadow-sm">
                Close
              </button>
            </div>

          </div>
        </div>,
        document.body
      )}

      {/* ── ENLARGED IMAGE MODAL ──────────────────────────────────────────── */}
      {enlargedImage && createPortal(
        <div 
          className="fixed top-0 left-0 w-full h-full z-[10000] flex items-center justify-center p-4 bg-fade-in" 
          onClick={() => setEnlargedImage(null)}
        >
          <button onClick={() => setEnlargedImage(null)} className="absolute top-6 right-6 text-white hover:text-red-400 bg-white/10 hover:bg-white/20 p-2 rounded-lg backdrop-blur-sm transition-all z-10">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          <img 
            src={enlargedImage} 
            alt="Enlarged Document" 
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl modal-blow-up" 
            onClick={(e) => e.stopPropagation()} 
          />
        </div>,
        document.body
      )}
      {isKycSuccessOpen && createPortal(
  <div className="fixed top-0 left-0 w-full h-full z-[9999] flex justify-center items-center bg-fade-in p-4">
    <div className="bg-white dark:bg-slate-800 w-full max-w-md rounded-2xl shadow-2xl p-8 text-center modal-blow-up border border-transparent dark:border-slate-700">
      
      <div className="w-16 h-16 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700/50 rounded-full flex items-center justify-center mx-auto mb-5">
        <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/>
        </svg>
      </div>

      <h3 className="text-xl font-extrabold text-gray-900 dark:text-slate-100 mb-2">
        KYC Submitted Successfully
      </h3>
      <p className="text-sm font-medium text-gray-500 dark:text-slate-400 leading-relaxed mb-8">
        Your documents have been securely uploaded. Our team will review and verify them shortly. You'll be notified once the process is complete.
      </p>

      <button
        onClick={() => {
          setIsKycSuccessOpen(false);
          setKycStatus('pending'); // now switch to pending card
        }}
        className="w-full py-3 rounded-xl text-sm font-bold text-white bg-green-600 hover:bg-green-700 transition-colors shadow-md active:scale-95"
      >
        Okay, Got it!
      </button>
    </div>
  </div>,
  document.body
)}

      <AlertModal isOpen={globalAlert.isOpen} title={globalAlert.title} message={globalAlert.message} onClose={() => setGlobalAlert({ ...globalAlert, isOpen: false })} />
    </div>
  );
};

export default KYC;